// OMNI Design System NumberInput Recipe for Chakra UI v3
// Provides consistent styling for NumberInput components based on OMNI design patterns
// Follows official Chakra UI v3 NumberInput component structure

import { defineSlotRecipe } from '@chakra-ui/react';

export const omniNumberInputRecipe = defineSlotRecipe({
  className: 'omni-numberinput',
  slots: [
    'root', // NumberInput.Root - Main container
    'input', // NumberInput.Input - The actual input field
    'control', // NumberInput.Control - The stepper controls container
    'incrementTrigger', // NumberInput.IncrementTrigger - Up arrow
    'decrementTrigger', // NumberInput.DecrementTrigger - Down arrow
  ],
  base: {
    root: {
      position: 'relative',
      display: 'flex',
      alignItems: 'stretch',
      width: '100%',
    },

    input: {
      // Inherit OMNI input styling from existing inputRecipe
      appearance: 'none',
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'border.default',
      backgroundColor: '#ffffff', // Pure white background like DateSelector
      color: '#2b3952', // navbar.500 - matching DateSelector exactly
      fontFamily: 'body',
      fontSize: 'md',
      fontWeight: 'normal',
      lineHeight: 'normal',
      outline: 'transparent',
      transition: 'all 200ms ease',
      width: '100%',
      minWidth: '0',
      flex: 1,

      // OMNI placeholder styling
      _placeholder: {
        color: 'fg.placeholder',
        opacity: 1,
      },

      // OMNI focus styling - matching DateSelector exactly
      _focus: {
        borderColor: '#00a1d2', // brand.500 
        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)', // Matching DateSelector
        backgroundColor: '#ffffff', // white
        color: '#2b3952', // navbar.500
        outline: 'none',
      },

      // Hover styles
      _hover: {
        borderColor: 'border.emphasis',
      },

      // Disabled styles
      _disabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
        backgroundColor: 'bg.muted',
        borderColor: 'border.subtle',
      },

      // Invalid/Error styles
      _invalid: {
        borderColor: 'red.500',
        boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
      },
    },

    control: {
      display: 'flex',
      flexDirection: 'column',
      borderLeft: '1px solid',
      borderColor: '#d1d5db', // gray.300 - matching DateSelector CSS
      backgroundColor: 'transparent', // Let individual buttons control their background
      borderTopRightRadius: 'base',
      borderBottomRightRadius: 'base',
    },

    incrementTrigger: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f0f2f5', // --rgb-tertiary from OMNI - matching DateSelector CSS
      border: '1px solid',
      borderColor: '#d1d5db', // gray.300 - matching DateSelector CSS
      borderTopRightRadius: 'base',
      borderBottom: '0.5px solid #d1d5db', // Half border between buttons
      cursor: 'pointer',
      outline: 'transparent',
      transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease', // OMNI transition
      color: '#2b3952', // navbar.500 - matching DateSelector CSS
      fontSize: 'xs',
      fontWeight: '600', // OMNI button font-weight - matching DateSelector CSS
      userSelect: 'none',

      // OMNI hover styling - matching DateSelector CSS exactly
      _hover: {
        backgroundColor: 'rgba(0, 161, 210, 0.15)', // --rgb-primary with 0.15 opacity
        borderColor: '#00a1d2', // brand.500
        color: '#3b3e3f', // --rgb-core-dark from OMNI
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // subtle shadow
      },

      // Active/pressed states - matching DateSelector CSS
      _active: {
        backgroundColor: 'rgba(0, 161, 210, 0.25)', // slightly more opacity
        borderColor: '#0091be', // brand.600
        color: '#3b3e3f',
        transform: 'translateY(1px)', // subtle press effect
      },

      _focus: {
        backgroundColor: 'rgba(0, 161, 210, 0.15)',
        borderColor: '#00a1d2',
        color: '#3b3e3f',
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '-2px',
      },

      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        backgroundColor: '#f0f2f5',
        color: 'fg.disabled',
      },
    },

    decrementTrigger: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f0f2f5', // --rgb-tertiary from OMNI - matching DateSelector CSS
      border: '1px solid',
      borderColor: '#d1d5db', // gray.300 - matching DateSelector CSS
      borderBottomRightRadius: 'base',
      borderTop: '0.5px solid #d1d5db', // Half border between buttons
      cursor: 'pointer',
      outline: 'transparent',
      transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease', // OMNI transition
      color: '#2b3952', // navbar.500 - matching DateSelector CSS
      fontSize: 'xs',
      fontWeight: '600', // OMNI button font-weight - matching DateSelector CSS
      userSelect: 'none',

      // OMNI hover styling - matching DateSelector CSS exactly
      _hover: {
        backgroundColor: 'rgba(0, 161, 210, 0.15)', // --rgb-primary with 0.15 opacity
        borderColor: '#00a1d2', // brand.500
        color: '#3b3e3f', // --rgb-core-dark from OMNI
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // subtle shadow
      },

      // Active/pressed states - matching DateSelector CSS
      _active: {
        backgroundColor: 'rgba(0, 161, 210, 0.25)', // slightly more opacity
        borderColor: '#0091be', // brand.600
        color: '#3b3e3f',
        transform: 'translateY(1px)', // subtle press effect
      },

      _focus: {
        backgroundColor: 'rgba(0, 161, 210, 0.15)',
        borderColor: '#00a1d2',
        color: '#3b3e3f',
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '-2px',
      },

      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        backgroundColor: '#f0f2f5',
        color: 'fg.disabled',
      },
    },
  },

  variants: {
    size: {
      xs: {
        input: {
          fontSize: '2xs',
          height: '6',
          paddingX: '2',
          paddingY: '1',
        },
        control: {
          width: '5',
        },
        incrementTrigger: {
          height: '3',
          fontSize: '2xs',
        },
        decrementTrigger: {
          height: '3',
          fontSize: '2xs',
        },
      },
      sm: {
        input: {
          fontSize: 'xs',
          height: '8',
          paddingX: '3',
          paddingY: '2',
        },
        control: {
          width: '6',
        },
        incrementTrigger: {
          height: '4',
          fontSize: 'xs',
        },
        decrementTrigger: {
          height: '4',
          fontSize: 'xs',
        },
      },
      md: {
        input: {
          fontSize: 'md',
          height: '10',
          paddingX: '4',
          paddingY: '2.5',
        },
        control: {
          width: '8',
        },
        incrementTrigger: {
          height: '5',
          fontSize: 'sm',
        },
        decrementTrigger: {
          height: '5',
          fontSize: 'sm',
        },
      },
      lg: {
        input: {
          fontSize: 'lg',
          height: '12',
          paddingX: '5',
          paddingY: '3',
        },
        control: {
          width: '10',
        },
        incrementTrigger: {
          height: '6',
          fontSize: 'md',
        },
        decrementTrigger: {
          height: '6',
          fontSize: 'md',
        },
      },
    },

    variant: {
      // Default OMNI style
      outline: {
        input: {
          borderColor: 'border.default',
          backgroundColor: 'bg.surface',
        },
        control: {
          borderColor: 'border.default',
          backgroundColor: 'bg.subtle',
        },
      },

      // Filled variant
      filled: {
        input: {
          borderColor: 'transparent',
          backgroundColor: 'bg.subtle',

          _focus: {
            borderColor: 'brand.500',
            backgroundColor: 'bg.surface',
          },

          _hover: {
            backgroundColor: 'bg.muted',
          },
        },
        control: {
          borderColor: 'transparent',
          backgroundColor: 'bg.muted',
        },
      },

      // Flushed variant (underlined)
      flushed: {
        input: {
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          borderRadius: '0',
          borderBottomWidth: '2px',
          borderBottomColor: 'border.default',
          paddingX: '0',

          _focus: {
            borderBottomColor: 'brand.500',
            boxShadow: 'none',
          },
        },
        control: {
          borderLeft: 'none',
          borderBottomWidth: '2px',
          borderBottomColor: 'border.default',
          backgroundColor: 'transparent',
          borderRadius: '0',
        },
      },

      // Unstyled variant
      unstyled: {
        input: {
          border: 'none',
          backgroundColor: 'transparent',
          borderRadius: '0',
          height: 'auto',
          paddingX: '0',
          paddingY: '0',

          _focus: {
            boxShadow: 'none',
          },
        },
        control: {
          border: 'none',
          backgroundColor: 'transparent',
          borderRadius: '0',
        },
      },
    },

    state: {
      // Success state
      success: {
        input: {
          borderColor: 'green.500',

          _focus: {
            borderColor: 'green.500',
            boxShadow: '0 0 0 0.125em rgba(44, 196, 173, 0.25)',
          },
        },
        control: {
          borderColor: 'green.500',
        },
      },

      // Warning state
      warning: {
        input: {
          borderColor: 'yellow.500',

          _focus: {
            borderColor: 'yellow.500',
            boxShadow: '0 0 0 0.125em rgba(254, 216, 130, 0.25)',
          },
        },
        control: {
          borderColor: 'yellow.500',
        },
      },

      // Error state
      error: {
        input: {
          borderColor: 'red.500',

          _focus: {
            borderColor: 'red.500',
            boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
          },
        },
        control: {
          borderColor: 'red.500',
        },
      },
    },
  },

  // Compound variants for combining variant + state
  compoundVariants: [
    {
      variant: 'filled',
      state: 'success',
      css: {
        input: {
          backgroundColor: 'green.50',
          borderColor: 'green.500',
        },
        control: {
          backgroundColor: 'green.100',
          borderColor: 'green.500',
        },
      },
    },
    {
      variant: 'filled',
      state: 'warning',
      css: {
        input: {
          backgroundColor: 'yellow.50',
          borderColor: 'yellow.500',
        },
        control: {
          backgroundColor: 'yellow.100',
          borderColor: 'yellow.500',
        },
      },
    },
    {
      variant: 'filled',
      state: 'error',
      css: {
        input: {
          backgroundColor: 'red.50',
          borderColor: 'red.500',
        },
        control: {
          backgroundColor: 'red.100',
          borderColor: 'red.500',
        },
      },
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'outline',
  },
});
